import { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import {
  Compass,
  TrendingUp,
  Newspaper,
  PieChart,
  Wrench,
  Shuffle,
  Sun,
  Moon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ThemeToggle } from '../ui/ThemeToggle'
import { useTheme } from '../theme-context'
import { Vercount } from '../Vercount'
import items from '../../data/item.json'
import { toolIdToSlug } from '../../lib/utils'

interface NavItem {
  label: string
  icon: LucideIcon
  path: string
  match?: (pathname: string) => boolean
}

const navItems: NavItem[] = [
  {
    label: '发现',
    icon: Compass,
    path: '/',
    match: (pathname) => pathname === '/',
  },
  {
    label: '价格行为',
    icon: TrendingUp,
    path: '/category/jiagexingwei',
    match: (pathname) => pathname.startsWith('/category/jiagexingwei'),
  },
  {
    label: '资讯',
    icon: Newspaper,
    path: '/category/zixun',
    match: (pathname) => pathname.startsWith('/category/zixun'),
  },
  {
    label: '期权',
    icon: PieChart,
    path: '/category/qiquan',
    match: (pathname) => pathname.startsWith('/category/qiquan'),
  },
  {
    label: '工具',
    icon: Wrench,
    path: '/category/gongju',
    match: (pathname) => pathname.startsWith('/category/gongju'),
  },
]

export function MainLayout({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()

  const navigateToRandomItem = () => {
    if (items.length === 0) return

    const currentSlug = location.pathname.startsWith('/item/')
      ? location.pathname.slice('/item/'.length).replace(/\/+$/, '')
      : null
    const candidates = items.filter(
      (item) => items.length === 1 || toolIdToSlug(item.id) !== currentSlug,
    )
    const item = candidates[Math.floor(Math.random() * candidates.length)]
    navigate(`/item/${toolIdToSlug(item.id)}`)
  }

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/20 transition-colors duration-300 font-sans">
      {/* Desktop / Tablet top nav — App Store web style */}
      <header className="hidden md:block sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-6 h-12 flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <img
              src="/logo.png"
              alt="6URL"
              width={28}
              height={28}
              decoding="async"
              fetchPriority="high"
              className="w-7 h-7 rounded-[22%] object-contain"
            />
            <span className="font-semibold text-[15px] tracking-tight">6URL</span>
          </Link>

          <nav className="flex items-center gap-1 flex-1">
            {navItems.map((item) => {
              const isActive = item.match
                ? item.match(location.pathname)
                : location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {item.label}
                </Link>
              )
            })}
          </nav>

          <button
            type="button"
            onClick={navigateToRandomItem}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label="随机发现一个网站"
          >
            <Shuffle className="w-3.5 h-3.5" />
            随机
          </button>

          <ThemeToggle />
        </div>
      </header>

      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-50 h-12 flex items-center justify-between px-4 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/logo.png"
            alt="6URL"
            width={28}
            height={28}
            decoding="async"
            className="w-7 h-7 rounded-[22%] object-contain"
          />
          <span className="font-semibold text-[15px] tracking-tight">6URL</span>
        </Link>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={navigateToRandomItem}
            className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-2 text-[12px] font-medium text-primary transition-all active:scale-95"
            aria-label="随机发现一个网站"
          >
            <Shuffle className="w-4 h-4" />
            随机
          </button>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="p-2 rounded-full bg-secondary text-primary transition-all active:scale-90"
            aria-label="切换主题"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <main className="pb-24 md:pb-0 min-h-[calc(100vh-3rem)]">{children}</main>

      <footer className="border-t border-border/50 pb-20 md:pb-0">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-3 px-5 py-5 text-[13px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between md:px-8 md:py-6 lg:px-10">
          <div>
            <p>想提交或添加新网站？欢迎申请加入。</p>
            <a
              href="mailto:support@6url.net?subject=申请加入 6URL"
              className="font-medium text-primary hover:underline underline-offset-4"
            >
              support@6url.net
            </a>
          </div>
          <Vercount className="text-left sm:text-right" />
        </div>
      </footer>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-background/85 backdrop-blur-xl border-t border-border/60 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-14">
          {navItems.map((item) => {
            const isActive = item.match
              ? item.match(location.pathname)
              : location.pathname === item.path
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors',
                  isActive ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.25 : 1.75} />
                <span className="text-[10px] font-medium leading-none">{item.label}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
